import { useEffect, useState } from 'react';

/**
 * PWA install UX (2026-09-16, Jaime-approved task).
 * Detects whether the app is already installed to the home screen and
 * captures the Android/Chrome `beforeinstallprompt` event so we can offer a
 * one-tap install from our own UI.
 *
 * Scope note: install state CANNOT be known across browsers/devices — this is
 * strictly per-device, persisted in localStorage so we never nag a browser
 * that is already running the app standalone (or where the user said no).
 */

const INSTALLED_KEY = 'vecindus_pwa_installed';

/**
 * Browser-aware install UX (Jaime 2026-09-23, corrected 02:10 UTC): modern iOS
 * (16.4+) lets users install PWAs from ANY browser via that browser's own
 * menu — Safari is NOT required. Each browser exposes "Add to Home Screen":
 * Chrome (⋮ menu → Añadir a pantalla de inicio), Edge/Firefox (same via their
 * menu). Pure function so detection is testable. Only meaningful when the
 * caller already knows the device is iOS.
 */
export type IosBrowser = 'safari' | 'chrome' | 'edge' | 'firefox' | 'other';

export function detectIosBrowser(ua: string): IosBrowser {
  // Chrome=CriOS, Firefox=FxiOS, Edge=EdgiOS (or Edg/), Opera=OPiOS/Opt,
  // Samsung=SamsungBrowser, DuckDuckGo=DuckDuckGo.
  if (/CriOS/i.test(ua)) return 'chrome';
  if (/EdgiOS|Edg\//i.test(ua)) return 'edge';
  if (/FxiOS/i.test(ua)) return 'firefox';
  if (/OPiOS|Opt\/|SamsungBrowser|DuckDuckGo/i.test(ua)) return 'other';
  return 'safari';
}

export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    // Android/Chrome/Edge standalone window.
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
    // iOS Safari: no display-mode support; navigator.standalone is the fallback.
    if ((window.navigator as unknown as { standalone?: boolean }).standalone === true) return true;
  } catch { /* very old browsers: treat as not installed */ }
  return false;
}

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

export function usePwaInstall() {
  const [installed, setInstalled] = useState<boolean>(() => {
    try { return localStorage.getItem(INSTALLED_KEY) === '1' || isStandaloneDisplay(); } catch { return false; }
  });
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIos, setIsIos] = useState(false);
  const [iosBrowser, setIosBrowser] = useState<IosBrowser>('safari');

  useEffect(() => {
    const ua = window.navigator.userAgent || '';
    // iOS Safari (iPhone/iPad/iPod) plus iPadOS 13+ which reports a Mac UA
    // but keeps multi-touch. iOS has no beforeinstallprompt → guided flow.
    const ios =
      /iphone|ipad|ipod/i.test(ua) ||
      (navigator.platform === 'MacIntel' && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints > 1);
    setIsIos(ios);
    setIosBrowser(detectIosBrowser(ua));

    // Already installed (persisted or standalone right now) → do nothing.
    try { if (localStorage.getItem(INSTALLED_KEY) === '1') { setInstalled(true); return; } } catch { /* private mode */ }
    if (isStandaloneDisplay()) {
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* ignore */ }
      setInstalled(true);
      return;
    }

    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault(); // suppress Chrome's mini-infobar; we show our own entry point
      setPromptEvent(e as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => {
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* ignore */ }
      setInstalled(true);
      setPromptEvent(null);
    };
    const onDisplayModeChange = (e: MediaQueryListEvent) => {
      if (e.matches) onAppInstalled();
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    const mq = window.matchMedia('(display-mode: standalone)');
    mq.addEventListener?.('change', onDisplayModeChange);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
      mq.removeEventListener?.('change', onDisplayModeChange);
    };
  }, []);

  /** One-tap install (Android/Chrome/desktop). prompt() may be called once per captured event. */
  const promptInstall = async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    if (!promptEvent) return 'unavailable';
    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      // prompt() is one-shot: drop the event regardless of outcome. If the
      // user dismissed the browser prompt, Chrome re-fires
      // beforeinstallprompt on a later visit, so we don't nag in-session.
      setPromptEvent(null);
      if (choice?.outcome === 'accepted') {
        try { localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* ignore */ }
        setInstalled(true);
        return 'accepted';
      }
      return 'dismissed';
    } catch {
      setPromptEvent(null);
      return 'unavailable';
    }
  };

  return {
    installed,
    canInstall: !!promptEvent,
    isIos,
    iosBrowser,
    promptInstall,
  };
}
